import * as d3 from "d3";
import { useEffect, useRef } from "react";
import { ColorAddedClub, IPlotclubComparisonData } from "../../types/ClubComparison";
import { IClubFilter, ClubPlotFilterOptions, IPossibleClubFilter } from "./ClubPlotUtils";
import {IClubKeyData} from "../../types/ClubComparison"

interface ScattrPlotProps {
  colorsAddedClubs: IPlotclubComparisonData[];
  filteredClubs: ColorAddedClub[];
  hoveredClub: number;
  setHoveredClub: React.Dispatch<React.SetStateAction<number>>;
  clubPlotFilters: IClubFilter;
  selectedClubs: number[];
  acrossSortDirection: "inc" | "dec";
  upSortDirection: "desc" | "asc";
  setSelectedClubs: React.Dispatch<React.SetStateAction<number[]>>;
  setClubCardID: React.Dispatch<React.SetStateAction<number>>;
  clubCardID: number;
}

function formatNumber(num: number) {
  const isNegative = num < 0;
  const absNum = Math.abs(num);
  let formattedNum: string;
  if (absNum >= 1000000) {
    formattedNum = `${(absNum / 1000000)?.toFixed(0)}M`;
  } else if (absNum >= 1000) {
    formattedNum = `${(absNum / 1000)?.toFixed(0)}K`;
  } else {
    formattedNum = absNum?.toString();
  }
  return isNegative ? `-${formattedNum}` : formattedNum;
}

export const makeStringsCapital = (str: string) => {
  const words = str?.split(/(?=[A-Z])/);
  const capitalizedWords = words?.map((word) => {
    return word.toUpperCase();
  });
  const result = capitalizedWords?.join(" ");
  return result;
};

export const ClubPlot: React.FC<ScattrPlotProps> = ({
  colorsAddedClubs,
  filteredClubs,
  hoveredClub,
  setHoveredClub,
  clubPlotFilters,
  selectedClubs,
  acrossSortDirection,
  upSortDirection,
  setSelectedClubs,
  setClubCardID,
  clubCardID
}) => {
  
  const minAcross = Math.min(
    ...colorsAddedClubs?.map((club : any) => club[clubPlotFilters?.across ? clubPlotFilters?.across : "teamTotalCosts"])
  );
  const maxAcross = Math.max(
    ...colorsAddedClubs?.map((club : any) => club[clubPlotFilters?.across ? clubPlotFilters?.across : "teamTotalCosts"])
  );
  const minUp = Math.min(...colorsAddedClubs?.map((club : any) => club[clubPlotFilters?.up ? clubPlotFilters?.up : "teamTotalRevenue"]));
  const maxUp = Math.max(...colorsAddedClubs?.map((club : any) => club[clubPlotFilters?.up ? clubPlotFilters?.up : "teamTotalRevenue"]));
  // console.log(minUp)
  // console.log(maxUp)
  const minDotSize = Math.min(
    ...colorsAddedClubs?.map((club : any) => club[clubPlotFilters?.dotSize ? clubPlotFilters?.dotSize : "teamAverageSystemFit"])
  );
  const maxDotSize = Math.max(
    ...colorsAddedClubs?.map((club : any) => club[clubPlotFilters?.dotSize ? clubPlotFilters?.dotSize : "teamAverageSystemFit"])
  );
  const svgRef = useRef(null);

  useEffect(() => {
    const container = document.getElementById("plot-graph")!;
    const pitchWidth: number = container.clientWidth;
    const pitchHeight: number = pitchWidth * 0.7;
    const svg: d3.Selection<SVGSVGElement, unknown, any, undefined> = d3
      .select<SVGSVGElement, unknown>(svgRef.current!)
      .attr("width", pitchWidth)
      .attr("height", pitchHeight)
      .on("click", () => setSelectedClubs([]));
    const numRows = 10;
    const numCols = 10;
    const data = d3.range(numRows * numCols).map((i) => ({
      x: (Math.floor(i % numCols) * pitchWidth) / 10,
      y: (Math.floor(i / numCols) * pitchHeight) / 10,
    }));
    svg.selectAll("*").remove();
    const gridGroup = svg.append("g").attr("transform", "translate(0, 0)");
    svg
      .append("rect")
      .attr("width", pitchWidth)
      .attr("height", pitchHeight)
      .attr("stroke-width", 1)
      .attr("stroke", "#DADADA")
      .attr("rx", 8)
      .style("fill", "transparent");
    gridGroup
      .selectAll("rect")
      .data(data)
      .enter()
      .append("rect")
      .attr("x", (d) => d.x)
      .attr("y", (d) => d.y)
      .attr("width", pitchWidth / 10)
      .attr("height", pitchHeight / 10)
      .attr("fill", (d, i) => {
        const row = Math.floor(i / numCols);
        const col = i % numCols;
        return (row + col) % 2 === 0 ? "#F9FAF7" : "#F2F5EE";
      });
    const xScale = d3
      .scaleLinear()
      .domain(acrossSortDirection === "inc" ? [maxAcross, minAcross] : [minAcross, maxAcross])
      .range([pitchWidth - 20, 20]);
    const yScale = d3
      .scaleLinear()
      .domain(upSortDirection === "desc" ? [minUp, maxUp] : [maxUp, minUp])
      .range([pitchHeight - 20, 20]);
    const zScale = d3.scaleLinear().domain([minDotSize, maxDotSize]).range([6, 40]);
    const brushGroup = svg.append("g").attr("class", "brush");
    const brush = d3
      .brush()
      .extent([
        [0, 0],
        [pitchWidth, pitchHeight],
      ])
      .on("start", function () {
        d3.select(this).attr("class", "selecting");
        d3.select(".scattr-plot-tooltip").style("visibility", "hidden");
      })
      .on("end", (event) => {
        if (!event.selection) return;
        const [[x0, y0], [x1, y1]] = event.selection;
        const selectedCircles = filteredClubs.filter((club : any) => {
          // console.log(club[clubPlotFilters?.up!])
          // console.log(yScale(club[clubPlotFilters?.up!]))
          const cx = clubPlotFilters?.across ? xScale(club[clubPlotFilters?.across]) : 0;
          const cy = clubPlotFilters?.across ? yScale(club[clubPlotFilters?.up!]) : yScale(club?.teamTotalRevenue);
          return cx >= x0 && cx <= x1 && cy >= y0 && cy <= y1;
        });
        const handleSelectedCircles = (selectedCircles: any[]) => {
          const selectedClubIds = selectedCircles.map((club) => club?.teamId);
          setSelectedClubs((prevSelected) => {
            const allAlreadySelected = selectedClubIds.every((id) => prevSelected.includes(id));
            if (allAlreadySelected) {
              return prevSelected.filter((id) => !selectedClubIds.includes(id));
            } else {
              const newSelected = Array.from(new Set([...prevSelected, ...selectedClubIds]));
              return newSelected;
            }
          });
        };
        handleSelectedCircles(selectedCircles);
        brushGroup.call(brush.move, null);
      });
    brushGroup.call(brush);
    const circleGroup = svg.append("g");
    let hoverTimeout: NodeJS.Timeout;
    filteredClubs?.map((club : any) =>
      circleGroup
        .append("circle")
        .attr("class", `plot-club-${club?.teamId}`)
        .attr("cx", clubPlotFilters?.across ? xScale(club[clubPlotFilters?.across]) : 20)
        .attr("cy", clubPlotFilters?.up ? yScale(club[clubPlotFilters?.up!]) : 20)
        .style(
          "fill",
          selectedClubs?.includes(club?.teamId)
            ? "#4AF075"
            : hoveredClub === 0 || hoveredClub === club?.teamId
            ? club?.colorCode
            : "white"
        )
        .style("stroke-width", 2)
        .style("cursor", "pointer")
        .style("stroke", selectedClubs?.includes(club?.teamId) ? "#4AF075" : club?.colorCode)
        .attr("r", clubPlotFilters?.dotSize ? zScale(club[clubPlotFilters?.dotSize]) : 6)
        .on("mouseenter", function () {
          if (d3.select(brushGroup.node()).classed("selecting")) return;
          hoverTimeout = setTimeout(() => {
            setHoveredClub(club?.teamId);
          }, 500);
        })
        .on("mouseout", function () {
          clearTimeout(hoverTimeout);
          setHoveredClub(0);
          d3.select(".scattr-plot-tooltip").style("visibility", "hidden");
        })
        .on("click", function () {
          if (clubCardID === club?.teamId) {
            setClubCardID(0);
          } else {
            setClubCardID(club?.teamId);
          }
        })
    );
    d3.select("body")
      .append("div")
      .attr("class", "scattr-plot-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background-color", "white")
      .style("border-radius", "2px");
  }, [colorsAddedClubs, hoveredClub, clubPlotFilters, selectedClubs, filteredClubs, upSortDirection, acrossSortDirection]);

  useEffect(() => {
    if (hoveredClub > 0) {
      const club :any = filteredClubs?.find((club) => club?.teamId === hoveredClub);
      const playerCircle = d3.select(`.plot-club-${club?.teamId}`);
      const radius = Number(playerCircle?.attr("r"));
      // @ts-ignore
      const boundingRect = playerCircle?.node()!.getBoundingClientRect();
      const x = Math.floor(boundingRect.left + window.scrollX);
      const y = Math.floor(boundingRect.top + window.scrollY);
      const leftOffset = radius > 35 ? -20 : radius > 17 ? -2 : radius > 12 ? 3 : radius > 10 ? 5 : radius > 9 ? 5 : 10;
      d3
        .select(".scattr-plot-tooltip")
        .style("visibility", "visible")
        .style("top", y - 70 + "px")
        .style("left", x - leftOffset + "px").html(`
            <div>
                <h4>${club?.teamLabel}</h4>
               <div>
        ${
          clubPlotFilters?.up
            ? `<p>${
               makeStringsCapital(ClubPlotFilterOptions.find(opt => opt.value === clubPlotFilters.up)?.label!)
              } - <span>${
                ClubPlotFilterOptions.find(opt => opt.value === clubPlotFilters.up)?.textKey
                  ? club![ClubPlotFilterOptions.find(opt => opt.value === clubPlotFilters.up)?.textKey! as IClubKeyData]
                  : formatNumber(club![clubPlotFilters.up])
              }</span></p>`
            : ''
        }
        ${
          clubPlotFilters?.across
            ? `<p>${
                makeStringsCapital(ClubPlotFilterOptions.find(opt => opt.value === clubPlotFilters.across)?.label!)
              } - <span>${
                ClubPlotFilterOptions.find(opt => opt.value === clubPlotFilters.across)?.textKey
                  ? club![ClubPlotFilterOptions.find(opt => opt.value === clubPlotFilters.across)?.textKey! as IClubKeyData]
                  : formatNumber(club![clubPlotFilters.across])
              }</span></p>`
            : ''
        }
        ${
          clubPlotFilters?.dotSize
            ? `<p>${
                makeStringsCapital(ClubPlotFilterOptions.find(opt => opt.value === clubPlotFilters.dotSize)?.label!)
              } - <span>${
                ClubPlotFilterOptions.find(opt => opt.value === clubPlotFilters.dotSize)?.textKey
                  ? club![ClubPlotFilterOptions.find(opt => opt.value === clubPlotFilters.dotSize)?.textKey! as IClubKeyData]
                  : formatNumber(club![clubPlotFilters.dotSize])
              }</span></p>`
            : ''
        }
      </div>
            </div>
        `);
    } else {
      d3.select(".scattr-plot-tooltip").style("visibility", "hidden");
    }
  }, [hoveredClub, colorsAddedClubs]);

  useEffect(() => {
    const handleMouseMove = (event: any) => {
      const circles = document.querySelectorAll(".plot-player");
      const plotPlayerTab = document.querySelectorAll(".plot-player-tab");
      let isOverPlayerTab = false;
      let isOverCircle = false;
      circles.forEach((circle) => {
        const rect = circle.getBoundingClientRect();
        if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
          isOverCircle = true;
        }
      });
      plotPlayerTab.forEach((tab) => {
        const rect = tab.getBoundingClientRect();
        if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
          isOverPlayerTab = true;
        }
      });

      if (!isOverCircle && !isOverPlayerTab) {
        setHoveredClub(0);
        d3.select(".scattr-plot-tooltip").style("visibility", "hidden");
      }
    };
    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <div className="plot-graph" id="plot-graph">
      {clubPlotFilters?.up && (
        <div className="plot-y-axis-label">
        {Array.from({ length: Math.min(10 + 1, maxUp - minUp + 1) }, (_, i) => {
          const value = upSortDirection === "desc" 
            ? Math.floor(maxUp - i * ((maxUp - minUp) / Math.min(10, maxUp - minUp)))
            : Math.floor(minUp + i * ((maxUp - minUp) / Math.min(10, maxUp - minUp)));
          return <span key={i}>{formatNumber(value)}</span>;
        })}
      </div>
      )}
      <svg ref={svgRef}></svg>
      {clubPlotFilters?.across && (
        <div className="plot-x-axis-label">
        {Array.from({ length: Math.min(10 + 1, maxAcross - minAcross + 1) }, (_, i) => {
          const value = acrossSortDirection === "inc"
            ? Math.floor(minAcross + i * ((maxAcross - minAcross) / Math.min(10, maxAcross - minAcross)))
            : Math.floor(maxAcross - i * ((maxAcross - minAcross) / Math.min(10, maxAcross - minAcross)));
          return <span key={i}>{formatNumber(value)}</span>;
        })}
      </div>
      )}
    </div>
  );
};
